import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Upload,
  Search,
  Trash2,
  Loader2,
  Image,
  Music,
  Video,
  FileText,
  ExternalLink,
  Copy,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../lib/api';

interface FileRecord {
  _id: string;
  url: string;
  originalName: string;
  category: string;
  size: number;
  uploadedBy?: { profile?: { name?: string }; email?: string };
}

const CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'image', label: 'Images' },
  { value: 'audio', label: 'Audio' },
  { value: 'video', label: 'Video' },
  { value: 'document', label: 'Documents' },
] as const;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function categoryIcon(category: string, size = 20) {
  switch (category) {
    case 'image':
      return <Image size={size} className="text-blue-500" />;
    case 'audio':
      return <Music size={size} className="text-purple-500" />;
    case 'video':
      return <Video size={size} className="text-red-500" />;
    default:
      return <FileText size={size} className="text-gray-500" />;
  }
}

export default function FilesPage() {
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['files', category, search, page],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit: 24 };
      if (category) params.category = category;
      if (search) params.search = search;
      const res = await api.get('/files', { params });
      return res.data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/files/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      toast.success('File deleted');
    },
    onError: () => toast.error('Failed to delete file'),
  });

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploading(true);
    let uploaded = 0;
    for (const file of Array.from(files)) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        await api.post('/files/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded++;
      } catch {
        toast.error(`Failed: ${file.name}`);
      }
    }
    if (uploaded > 0) {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      toast.success(`${uploaded} file${uploaded > 1 ? 's' : ''} uploaded`);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied');
  };

  const filesList = data?.data ?? [];
  const pagination = data?.pagination ?? { total: 0, page: 1, totalPages: 1 };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Files</h1>
          <p className="text-gray-500 mt-1">{pagination.total} files uploaded</p>
        </div>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="btn-primary flex items-center gap-2"
        >
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
          {uploading ? 'Uploading...' : 'Upload Files'}
        </button>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleUpload} />
      </div>

      <div className="card">
        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search files..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="input-field pl-9"
            />
          </div>
          <div className="flex gap-1">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.value}
                onClick={() => {
                  setCategory(cat.value);
                  setPage(1);
                }}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  category === cat.value
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* File grid */}
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 size={24} className="animate-spin text-gray-400" />
          </div>
        ) : filesList.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400">
            <FileText size={40} className="mb-2" />
            <p>No files found</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {filesList.map((file: FileRecord) => (
              <div
                key={file._id}
                className="group relative border border-gray-200 rounded-lg p-3 hover:border-primary-300 hover:shadow-sm transition-all"
              >
                {/* Preview */}
                <div className="aspect-square bg-gray-50 rounded-lg flex items-center justify-center mb-2 overflow-hidden">
                  {file.category === 'image' ? (
                    <img
                      src={file.url}
                      alt={file.originalName}
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    categoryIcon(file.category, 32)
                  )}
                </div>

                {/* Info */}
                <p className="text-xs text-gray-800 truncate font-medium" title={file.originalName}>
                  {file.originalName}
                </p>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[10px] text-gray-400">{formatSize(file.size)}</span>
                  <span className="text-[10px] text-gray-400 capitalize">{file.category}</span>
                </div>
                {file.uploadedBy && (
                  <p className="text-[10px] text-gray-400 truncate mt-0.5">
                    by {file.uploadedBy.profile?.name || file.uploadedBy.email}
                  </p>
                )}

                {/* Actions overlay */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => copyUrl(file.url)}
                    className="p-1 bg-white/90 rounded shadow-sm text-gray-500 hover:text-primary-600"
                    title="Copy URL"
                  >
                    <Copy size={12} />
                  </button>
                  <a
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1 bg-white/90 rounded shadow-sm text-gray-500 hover:text-primary-600"
                    title="Open in new tab"
                  >
                    <ExternalLink size={12} />
                  </a>
                  <button
                    onClick={() => deleteMutation.mutate(file._id)}
                    className="p-1 bg-white/90 rounded shadow-sm text-gray-500 hover:text-danger-500"
                    title="Delete"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="btn-secondary text-xs"
              >
                Prev
              </button>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page >= pagination.totalPages}
                className="btn-secondary text-xs"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
